import React from 'react';
import { resendOtp, signIn, verifyOtp } from './customerSession';
import * as s from './portalStyles';
import PortalBrand from './PortalBrand';

/// The customer half of sign-in, at /portal. Internal staff never reach it — they use the Entra
/// button on the root screen (AuthGate.js). Two separate doors rather than one email-first form
/// that decides for you: routing on the address typed in would answer "is this address a
/// provisioned contact?" for anyone who asked.
///
/// Two steps, not one: `signIn` resolves to `{ otpRequired: true }` on a browser the account
/// hasn't earned trust on yet (Phase D, ADR 0037), and this switches to the code-entry form rather
/// than calling `onSignedIn` — that only happens once `verifyOtp` (or a trusted-device `signIn`)
/// actually returns tokens.
export default function CustomerSignIn({ onSignedIn, onForgotPassword, notice }) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [awaitingCode, setAwaitingCode] = React.useState(false);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [resent, setResent] = React.useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result = await signIn(email, password);
      if (result.otpRequired) {
        setAwaitingCode(true);
      } else {
        onSignedIn();
      }
    } catch (err) {
      setError(err.message);
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await verifyOtp(email, code);
      onSignedIn();
    } catch (err) {
      setError(err.message);
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    setError('');
    setResent(false);
    try {
      await resendOtp(email);
      setResent(true);
    } catch (err) {
      setError(err.message);
    }
  }

  function backToPassword() {
    setAwaitingCode(false);
    setCode('');
    setError('');
    setResent(false);
  }

  if (awaitingCode) {
    return (
      <div style={s.page}>
        <PortalBrand />
        <div style={s.subtitle}>Aumentum Technologies · Customer Portal</div>
        <div style={s.heading}>Check your email</div>
        <div style={s.helpText}>
          We don't recognize this browser, so we've sent a 6-digit code to <strong>{email}</strong>.
          Enter it below — it's valid for 10 minutes.
        </div>

        {error && <div style={s.errorBox}>⚠ {error}</div>}
        {resent && !error && <div style={s.noticeBox}>A new code is on its way.</div>}

        <form onSubmit={handleVerify}>
          <div className="form-group">
            <label className="form-label" htmlFor="portal-otp">Verification code</label>
            <input
              id="portal-otp"
              className="form-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <button
            className="btn btn-primary" style={s.submitButton} type="submit"
            disabled={busy || code.length !== 6}
          >
            {busy ? 'Verifying…' : 'Verify and sign in'}
          </button>
        </form>

        <div style={s.footerRow}>
          <button type="button" style={s.linkButton} onClick={handleResend}>
            Resend code
          </button>
          <button type="button" style={s.linkButton} onClick={backToPassword}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <PortalBrand />
      <div style={s.subtitle}>Aumentum Technologies · Customer Portal</div>

      {notice && <div style={s.noticeBox}>{notice}</div>}
      {error && <div style={s.errorBox}>⚠ {error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="portal-email">Email address</label>
          <input
            id="portal-email"
            className="form-input"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="portal-password">Password</label>
          <input
            id="portal-password"
            className="form-input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" style={s.submitButton} type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div style={s.footerRow}>
        <button type="button" style={s.linkButton} onClick={onForgotPassword}>
          Forgot your password?
        </button>
        <a href="/" style={{ ...s.linkButton, textDecoration: 'none' }}>
          Aumentum staff sign-in
        </a>
      </div>
    </div>
  );
}
